import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { OrderDto } from "@dishfolio/contracts";
import { api, withOrg } from "./api.ts";

/** Short two-tone chime via WebAudio (no audio files to load). */
export function chime() {
  try {
    const ctx = new AudioContext();
    const tone = (freq: number, at: number) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.frequency.value = freq;
      o.connect(g);
      g.connect(ctx.destination);
      g.gain.setValueAtTime(0.0001, ctx.currentTime + at);
      g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + at + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + at + 0.4);
      o.start(ctx.currentTime + at);
      o.stop(ctx.currentTime + at + 0.45);
    };
    tone(880, 0);
    tone(1320, 0.18);
    setTimeout(() => void ctx.close(), 1000);
  } catch {
    /* audio blocked until user interaction */
  }
}

/**
 * The last 12 hours of a location's orders, kept current by the order event stream (polling is the
 * fallback while the stream reconnects). Shared by the orders board, the kitchen display and the POS.
 */
export function useLiveOrders(restaurantId: string | undefined, opts: { enabled: boolean; onNew?: (o: OrderDto) => void }) {
  const qc = useQueryClient();
  const [live, setLive] = useState(false);
  const key = useMemo(() => ["orders", restaurantId], [restaurantId]);
  const since = useMemo(() => new Date(Date.now() - 12 * 3600_000).toISOString(), [restaurantId]);
  const enabled = Boolean(restaurantId && opts.enabled);
  const orders = useQuery({
    queryKey: key,
    queryFn: () => api.get<{ orders: OrderDto[] }>(`/restaurants/${restaurantId}/orders?from=${encodeURIComponent(since)}&limit=300`).then((x) => x.orders),
    enabled,
    refetchInterval: live ? 60_000 : 10_000,
  });
  const onNew = useRef(opts.onNew);
  onNew.current = opts.onNew;

  const upsert = (o: OrderDto) =>
    qc.setQueryData<OrderDto[]>(key, (prev) => {
      const list = prev ?? [];
      const i = list.findIndex((x) => x.id === o.id);
      return i >= 0 ? list.map((x) => (x.id === o.id ? o : x)) : [o, ...list];
    });

  useEffect(() => {
    if (!enabled) return;
    const es = new EventSource(withOrg(`/api/v1/restaurants/${restaurantId}/orders/stream`), { withCredentials: true });
    es.addEventListener("ready", () => {
      setLive(true);
      // Catch up on anything missed while disconnected.
      void qc.invalidateQueries({ queryKey: key });
    });
    es.addEventListener("order.created", (e) => {
      const o = JSON.parse((e as MessageEvent).data) as OrderDto;
      const known = qc.getQueryData<OrderDto[]>(key)?.some((x) => x.id === o.id);
      upsert(o);
      if (!known) onNew.current?.(o);
    });
    es.addEventListener("order.updated", (e) => upsert(JSON.parse((e as MessageEvent).data)));
    // Seating changes (seat, move, clear) from other devices refresh the floor.
    es.addEventListener("table.updated", () => void qc.invalidateQueries({ queryKey: ["floor", restaurantId] }));
    es.onerror = () => setLive(false);
    return () => es.close();
  }, [enabled, restaurantId]);

  return { orders, live, upsert, key };
}
